import { uiMessage } from '../../utils/uiTranslation';
import { uiText, useUiLanguage } from '../../utils/uiTranslation';
import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../firebase';
import { getCompanyId } from '../../services/outreachService';

/**
 * OAuth callback handler for connecting sender emails (Gmail/Outlook)
 * This component receives the OAuth code from the callback URL and exchanges it for tokens
 */
const OutreachEmailCallback = () => {
  useUiLanguage();
    const location = useLocation();
    const navigate = useNavigate();
    const [status, setStatus] = useState('processing');
    const [message, setMessage] = useState('Connecting your email account...');
    const hasProcessedRef = useRef(false);

    useEffect(() => {
        if (hasProcessedRef.current) return;
        hasProcessedRef.current = true;
        const handleCallback = async () => {
            try {
                // Get URL parameters
                const params = new URLSearchParams(location.search);
                const code = params.get('code');
                const state = params.get('state');
                const error = params.get('error');

                if (error) {
                    setStatus('error');
                    setMessage(uiMessage('Authorization failed. Please try connecting again.'));
                    return;
                }

                if (!code) {
                    setStatus('error');
                    setMessage('No authorization code received');
                    return;
                }

                // Wait for auth to be ready
                const user = await new Promise((resolve) => {
                    const unsubscribe = onAuthStateChanged(auth, (u) => {
                        unsubscribe();
                        resolve(u);
                    });
                });

                if (!user) {
                    setStatus('error');
                    setMessage('Not authenticated. Please log in first.');
                    return;
                }

                // Get company ID
                const companyId = await getCompanyId();

                // Determine provider from state
                const provider = state?.includes('gmail') || state?.includes('google') ? 'google' : 'microsoft';

                // Call backend to exchange code
                const API_BASE_URL = process.env.REACT_APP_BACKEND_URL_PROD || 'https://employer-web-backend-435586021357.us-central1.run.app';

                const response = await fetch(`${API_BASE_URL}/outreach/sender/callback`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        company_uid: companyId,
                        code: code,
                        provider: provider
                    })
                });

                const result = await response.json();

                if (result.success) {
                    setStatus('success');
                    setMessage(uiMessage('Successfully connected: {{email}}', { email: result.email || '' }));

                    // Redirect back to outreach after 2 seconds
                    setTimeout(() => {
                        navigate('/outreach', { replace: true });
                    }, 2000);
                } else {
                    setStatus('error');
                    setMessage('Failed to connect email account');
                }

            } catch (err) {
                console.error('OAuth callback error:', err);
                setStatus('error');
                setMessage('An error occurred while connecting your email account');
            }
        };

        handleCallback();
    }, [location, navigate]);

    return (
        <div style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'var(--app-background, #031128)',
            fontFamily: 'Montserrat, sans-serif'
        }}>
            <div style={{
                background: 'var(--app-surface, rgba(255,255,255,0.05))',
                borderRadius: '16px',
                padding: '40px',
                textAlign: 'center',
                maxWidth: '400px',
                width: '100%'
            }}>
                {status === 'processing' && (
                    <>
                        <i className="fa-solid fa-spinner fa-spin" style={{ fontSize: '48px', color: '#568ded', marginBottom: '24px' }}></i>
                        <h2 style={{ color: 'var(--app-text, white)', marginBottom: '12px' }}>{uiText("Connecting...")}</h2>
                        <p style={{ color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.6)' }}>{uiText(message)}</p>
                    </>
                )}

                {status === 'success' && (
                    <>
                        <i className="fa-solid fa-check-circle" style={{ fontSize: '48px', color: 'var(--app-tone-2ecc71, #2ecc71)', marginBottom: '24px' }}></i>
                        <h2 style={{ color: 'var(--app-text, white)', marginBottom: '12px' }}>{uiText("Connected!")}</h2>
                        <p style={{ color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.6)' }}>{uiText(message)}</p>
                        <p style={{ color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.4)', fontSize: '13px', marginTop: '16px' }}>{uiText("Redirecting to Outreach...")}</p>
                    </>
                )}

                {status === 'error' && (
                    <>
                        <i className="fa-solid fa-exclamation-triangle" style={{ fontSize: '48px', color: 'var(--app-tone-e74c3c, #e74c3c)', marginBottom: '24px' }}></i>
                        <h2 style={{ color: 'var(--app-text, white)', marginBottom: '12px' }}>{uiText("Connection Failed")}</h2>
                        <p style={{ color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.6)' }}>{uiText(message)}</p>
                        <button
                            onClick={() => navigate('/outreach', { replace: true })}
                            style={{ '--light-control-fill': '#568ded',
                                marginTop: '24px',
                                padding: '12px 24px',
                                background: '#568ded',
                                border: 'none',
                                borderRadius: '8px',
                                color: 'white',
                                fontFamily: 'Montserrat',
                                fontWeight: 600,
                                cursor: 'pointer'
                            }}
                          data-light-control={"filled"}>{uiText("Return to Outreach")}</button>
                    </>
                )}
            </div>
        </div>
    );
};

export default OutreachEmailCallback;
